import { Component, Input, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Chart } from 'chart.js';
import { SalesService } from '@shared/services/sales.service';
import { Utils } from '@shared/utils';
import { InputSwitchModule } from 'primeng/inputswitch';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-quadrant-performance-matrix',
    imports: [CommonModule, FormsModule, InputSwitchModule],
  templateUrl: './quadrant-performance-matrix.component.html',
  styleUrl: './quadrant-performance-matrix.component.scss'
})
export class QuadrantPerformanceMatrixComponent implements AfterViewInit {
    @Input() dateStart!: Date;
    @Input() dateEnd!: Date;
    @ViewChild('chartCanvas') chartCanvas!: ElementRef<HTMLCanvasElement>;

    todayStart!: Date;
    todayEnd!: Date;
    topSellableItems: any[]=[];
    topProfitableItems: any[]=[];
    mergedItems: any[]=[];
    isTopItemsLoading: boolean = true;
    useNetProfit: boolean = false;
    showLowestItems: boolean = false;
    profitablesLoaded: boolean = false;
    sellablesLoaded: boolean = false;
    chart: Chart | null = null;
    storefrontColors: Map<string, string> = new Map();

    constructor(private salesService: SalesService) {
    }

    ngOnInit() {
        this.todayStart = this.dateStart || new Date(new Date().toDateString());
        this.todayEnd = this.dateEnd || new Date(this.todayStart.getTime() + (1000 * 60 * 60 * 24));
        
        this.getTopProfitables();
        this.getTopSellables();
    }

    ngAfterViewInit() {
        // Chart will be initialized after data is merged
    }

    getTopProfitables() {
        this.isTopItemsLoading = true;
        this.salesService.getTopProfitables(this.todayStart, this.todayEnd, 6, this.useNetProfit, this.showLowestItems)
            .subscribe((topItems) => {
                this.topProfitableItems = topItems;
                this.profitablesLoaded = true;
                this.checkAndMerge();
            });
    }
    
    getTopSellables() {
        this.isTopItemsLoading = true;
        this.salesService.getTopSellables(this.todayStart, this.todayEnd, 6, this.useNetProfit, this.showLowestItems)
            .subscribe((topItems) => {
                this.topSellableItems = topItems;
                this.sellablesLoaded = true;
                this.checkAndMerge();
            });
    }

    onLowestItemsToggle() {
        this.getTopProfitables();
        this.getTopSellables();
    }

    checkAndMerge() {
        if (this.profitablesLoaded && this.sellablesLoaded) {
            this.mergeLists();
            this.isTopItemsLoading = false;
            // Initialize chart after merge
            setTimeout(() => this.initializeChart(), 0);
            // Reset flags for next load
            this.profitablesLoaded = false;
            this.sellablesLoaded = false;
        }
    }

    mergeLists() {
        const mergedMap = new Map();

        // Add profitable items first
        this.topProfitableItems.forEach(item => {
            mergedMap.set(item.sku, { ...item });
        });

        // Merge sellable items by SKU
        this.topSellableItems.forEach(item => {
            if (mergedMap.has(item.sku)) {
                // Merge with existing profitable item
                // mergedMap.set(item.sku, {
                //     ...mergedMap.get(item.sku),
                //     ...item,
                //     // Keep profitable item's profit if sellable doesn't have it
                //     profit: item.profit || mergedMap.get(item.sku).profit,
                //     quantity: (mergedMap.get(item.sku).quantity || 0) + (item.quantity || 0)
                // });
            } else {
                // Add new sellable item
                mergedMap.set(item.sku, { ...item });
            }
        });

        this.mergedItems = Array.from(mergedMap.values());
    }

    initializeChart() {
        if (!this.chartCanvas || !this.chartCanvas.nativeElement) {
            return;
        }

        // Destroy existing chart if it exists
        if (this.chart) {
            this.chart.destroy();
        }

        // Generate storefront colors
        //this.generateStorefrontColors();

        // Transform merged items into bubble chart data
        const chartData = this.transformDataForBubbleChart();

        const ctx = this.chartCanvas.nativeElement.getContext('2d');
        if (!ctx) return;

        const bubbleLabelsPlugin = {
            id: 'bubbleLabels',
            afterDatasetsDraw: (chart: any) => {
                const ctx = chart.ctx;
                chart.data.datasets.forEach((dataset: any) => {
                    dataset.data.forEach((datapoint: any) => {
                        const x = chart.scales.x.getPixelForValue(datapoint.x);
                        const y = chart.scales.y.getPixelForValue(datapoint.y);

                        ctx.font = 'normal 13px Arial';
                        ctx.fillStyle = '#3b13cd';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'bottom';
                        ctx.fillText(datapoint.sku, x, y - datapoint.r - 2);
                    });
                });
            }
        };

        this.chart = new Chart(ctx, {
            type: 'bubble',
            data: {
                datasets: chartData
            },
            plugins: [bubbleLabelsPlugin],
            options: {
                responsive: true,
                maintainAspectRatio: true,
                aspectRatio: 1,
                plugins: {
                    legend: {
                        display: false,
                        position: 'top'
                    },
                    tooltip: {
                        callbacks: {
                            label: (context: any) => {
                                const item = context.raw;
                                return [
                                    `Name: ${item.name}`,
                                    `SKU: ${item.sku}`,
                                    `Profit: $${item.profit.toFixed(2)}`,
                                    `Sales: ${item.quantity}`,
                                    `Quantity: ${item.stockQty}`
                                ];
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        title: {
                            display: true,
                            text: 'Sales '
                        }
                    },
                    y: {
                        title: {
                            display: true,
                            text: 'Profit ($)'
                        }
                    }
                }
            }
        });
    }

    // generateStorefrontColors() {
    //     const colors = [
    //         '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
    //         '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
    //     ];
    //     let colorIndex = 0;

    //     this.mergedItems.forEach(item => {
    //         if (item.storefront && !this.storefrontColors.has(item.storefront)) {
    //             this.storefrontColors.set(item.storefront, colors[colorIndex % colors.length]);
    //             colorIndex++;
    //         }
    //     });
    // }

    transformDataForBubbleChart(): any[] {
        const storefrontGroups = new Map<string, any[]>();

        // Group items by storefront
        this.mergedItems.forEach(item => {
            const storefront = item.storefront || 'Unknown';
            if (!storefrontGroups.has(storefront)) {
                storefrontGroups.set(storefront, []);
            }

            const radius = Math.max(5, Math.log(item.stockQty || 1) * 3); // Scale log stock qty for bubble size

            storefrontGroups.get(storefront)?.push({
                x: item.quantity || 0,
                y: item.profit || 0,
                r: radius,
                name: item.name,
                sku: item.sku,
                profit: item.profit,
                quantity: item.quantity,
                stockQty: item.stockQty
            });
        });

        // Create datasets for each storefront
        const datasets: any[] = [];
        storefrontGroups.forEach((items, storefront) => {
             const color = Utils.storeFrontColors[storefront as keyof typeof Utils.storeFrontColors] || '#000000'; // Default color
             const bgColorWithOpacity = this.hexToRgba(color, 0.6); // Add 60% opacity
            
            datasets.push({
                label: storefront,
                data: items,
                backgroundColor: bgColorWithOpacity,
                borderColor: color,
                borderWidth: 1.5
            });
        });

        return datasets;
    }

    hexToRgba(hex: string, alpha: number): string {
        // Remove # if present
        hex = hex.replace('#', '');
        
        // Parse hex values
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
}
